import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { productContext, trackEvent } from '@theme/analytics-events';

/** The "Custom" chip's stored value; fixed English like every line item property. */
const SIZE_LATER = 'Confirm size later';

/**
 * @typedef {object} CardQuickBuyRefs
 * @property {HTMLFormElement} form - The /cart/add form.
 * @property {HTMLFieldSetElement} sizes - Ring size chips (empty apart from the photo note for non-rings).
 * @property {HTMLButtonElement} addButton - "+ Quick add".
 * @property {HTMLElement} status - Polite status for the result.
 */

/**
 * Instant commission from a product card. Adds the piece with the same cart contract as the product
 * form: it dispatches a CartLinesUpdateEvent whose promise resolves with the refreshed cart and the
 * rendered cart sections, so the cart drawer morphs and opens. Rings need a size; the first "Quick add"
 * without one opens the chips instead of adding. Cards without size chips add straight away.
 *
 * @extends Component<CardQuickBuyRefs>
 */
class CardQuickBuy extends Component {
  requiredRefs = ['form', 'sizes', 'addButton', 'status'];

  /** @type {AbortController | null} */
  #abortController = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /** @param {Event} event */
  sizeChosen(event) {
    this.#announce('');
    if (event.target instanceof HTMLInputElement && event.target.value === SIZE_LATER) {
      trackEvent('size_later_selected', { ...productContext(this.refs.form), source: 'quick_buy' }, { once: true });
    }
  }

  /** @param {SubmitEvent} event */
  async add(event) {
    event.preventDefault();

    const { form, sizes, addButton } = this.refs;
    if (addButton.getAttribute('aria-busy') === 'true') return;

    const needsSize = sizes.querySelector('input[type="radio"]') !== null;
    const chosen = sizes.querySelector('input:checked');
    if (needsSize && !chosen) {
      this.setAttribute('data-open', '');
      this.#announce(this.dataset.chooseSize ?? '');
      sizes.querySelector('input')?.focus();
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    addButton.setAttribute('aria-busy', 'true');

    const formData = new FormData(form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: /** @type {string} */ (formData.get('id')), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, { ...fetchConfig('javascript', { body: formData }), signal });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Quick add failed');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { signal });
      if (!cartResponse.ok) throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
      const ajaxCart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'card-quick-buy',
          sections: result.sections,
          didError: false,
        },
      });
      this.removeAttribute('data-open');
      this.#announce(this.dataset.added ?? '');
    } catch (error) {
      deferred.reject(error);
      if (error instanceof Error && error.name === 'AbortError') return;

      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Quick add failed',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#announce(this.dataset.error ?? '');
    } finally {
      addButton.removeAttribute('aria-busy');
    }
  }

  /** @param {string} message */
  #announce(message) {
    this.refs.status.textContent = message;
  }
}

if (!customElements.get('card-quick-buy')) {
  customElements.define('card-quick-buy', CardQuickBuy);
}
